import { Head, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import { EditItemDialog } from '@/components/reimbursement-items/EditItemDialog'
import { MatchedItemCard } from '@/components/reimbursement-items/MatchedItemCard'
import { DuplicateWarning } from '@/components/reimbursement-items/DuplicateWarning'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import type { ReimbursementItem, ReimbursementItemGroup, MatchedItem, DuplicateGroups, Category } from '@/types'
import {
  Inbox,
  Upload,
  Check,
  Trash2,
  Loader2,
  FileText,
  AlertCircle,
  RefreshCw,
  Pencil,
  ImageIcon,
  Link2,
} from 'lucide-react'
import { useState, useCallback, useRef, useEffect } from 'react'
import type { DragEvent, ChangeEvent } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  matched_items: MatchedItem[]
  duplicate_groups: DuplicateGroups
  grouped_items: ReimbursementItemGroup[]
  categories: Category[]
  pending_count: number
}

function formatDate(iso: string): string {
  const date = new Date(iso + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.getTime() === today.getTime()) return 'Today'
  if (date.getTime() === yesterday.getTime()) return 'Yesterday'

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

const statusConfig: Record<string, { label: string; className: string }> = {
  pending_processing: { label: 'Processing', className: 'bg-primary/10 text-primary border-primary/20' },
  pending_review: { label: 'Ready', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  failed: { label: 'Failed', className: 'bg-destructive/10 text-destructive border-destructive/20' },
}

export default function ReimbursementItemsIndex({ matched_items, duplicate_groups, grouped_items, categories, pending_count }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [editItem, setEditItem] = useState<ReimbursementItem | null>(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [clearDialogOpen, setClearDialogOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-poll while items are processing
  useEffect(() => {
    if (pending_count === 0) return
    const interval = setInterval(() => {
      router.reload({ only: ['grouped_items', 'matched_items', 'duplicate_groups', 'pending_count'] })
    }, 3000)
    return () => clearInterval(interval)
  }, [pending_count])

  const handleFiles = useCallback((files: FileList) => {
    setUploading(true)
    const data: Record<string, File[]> = { receipts: Array.from(files) }
    router.post('/reimbursement_items/upload', data, {
      forceFormData: true,
      onFinish: () => setUploading(false),
    })
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles])

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) handleFiles(files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [handleFiles])

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleGroupSelection(group: ReimbursementItemGroup, checked: boolean) {
    const reviewableIds = group.items
      .filter((i) => i.status === 'pending_review')
      .map((i) => i.id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      reviewableIds.forEach((id) => checked ? next.add(id) : next.delete(id))
      return next
    })
  }

  function isGroupFullySelected(group: ReimbursementItemGroup) {
    const reviewable = group.items.filter((i) => i.status === 'pending_review')
    if (reviewable.length === 0) return false
    return reviewable.every((i) => selectedIds.has(i.id))
  }

  function handleApprove(id: number) {
    router.post(`/reimbursement_items/${id}/approve`, {}, { preserveScroll: true })
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next })
  }

  function handleApproveGroup(group: ReimbursementItemGroup) {
    const ids = group.items.filter((i) => i.status === 'pending_review').map((i) => i.id)
    if (ids.length === 0) return
    router.post('/reimbursement_items/approve_bulk', { item_ids: ids }, { preserveScroll: true })
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.delete(id))
      return next
    })
  }

  function handleBulkApprove() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    router.post('/reimbursement_items/approve_bulk', { item_ids: ids }, { preserveScroll: true })
    setSelectedIds(new Set())
  }

  function handleRetry(id: number) {
    router.post(`/reimbursement_items/${id}/retry_processing`, {}, { preserveScroll: true })
  }

  function handleDelete(id: number) {
    router.delete(`/reimbursement_items/${id}`, { preserveScroll: true })
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next })
  }

  function handleClearInbox() {
    router.delete('/reimbursement_items/clear', {
      preserveScroll: true,
      onSuccess: () => setSelectedIds(new Set()),
    })
  }

  const totalItems = grouped_items.reduce((sum, g) => sum + g.items.length, 0)
  const hasMatchedItems = matched_items.length > 0
  const hasDuplicates = duplicate_groups.item_duplicates.length > 0 || duplicate_groups.transaction_duplicates.length > 0
  const inboxItemCount = totalItems + matched_items.length
  const isEmpty = totalItems === 0 && pending_count === 0 && !hasMatchedItems && !hasDuplicates

  return (
    <>
      <Head title="Inbox" />
      <PageHeader title="Inbox" description="Upload receipts and match to transactions">
        <Button
          variant="destructive"
          size="sm"
          disabled={inboxItemCount === 0}
          onClick={() => setClearDialogOpen(true)}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Clear Inbox
        </Button>
      </PageHeader>

      <PageContent>
        {/* Drop zone */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={cn(
            'mb-6 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors',
            'flex flex-col items-center justify-center gap-2',
            dragging ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30',
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
              <span className="text-[13px] text-muted-foreground">Uploading receipts...</span>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-muted-foreground/60" />
              <span className="text-[13px] text-muted-foreground">
                Drop receipt files here or click to select
              </span>
              <span className="text-[11px] text-muted-foreground/60">
                Supports images and PDFs. Multiple files at once.
              </span>
            </>
          )}
        </div>

        {/* Processing indicator */}
        {pending_count > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
            <Loader2 className="h-4 w-4 text-primary animate-spin" />
            <span className="text-[13px] text-primary">
              {pending_count} receipt{pending_count !== 1 ? 's' : ''} being analyzed...
            </span>
          </div>
        )}

        {/* Suggested Matches */}
        {hasMatchedItems && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-4 w-4 text-primary" />
              <h2 className="text-[14px] font-medium text-foreground">Suggested Matches</h2>
              <span className="text-[12px] text-muted-foreground">
                {matched_items.length} receipt{matched_items.length !== 1 ? 's' : ''} matched to SBI transactions
              </span>
            </div>
            <div className="space-y-2">
              {matched_items.map((matchedItem) => (
                <MatchedItemCard
                  key={matchedItem.item.id}
                  match={matchedItem}
                  categories={categories}
                />
              ))}
            </div>
          </div>
        )}

        {/* Potential Duplicates */}
        <DuplicateWarning duplicates={duplicate_groups} categories={categories} />

        {/* Empty state */}
        {isEmpty && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-3.5">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-3 text-[15px] font-medium text-foreground">Inbox is empty</h3>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Drop receipt files above to get started.
            </p>
          </div>
        )}

        {/* Grouped items */}
        {grouped_items.map((group) => {
          const reviewableCount = group.items.filter((i) => i.status === 'pending_review').length

          return (
            <div key={group.date} className="mb-6">
              {/* Day header */}
              <div className="flex items-center justify-between rounded-t-lg border border-b-0 bg-muted/40 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {reviewableCount > 0 && (
                    <Checkbox
                      checked={isGroupFullySelected(group)}
                      onCheckedChange={(checked) => toggleGroupSelection(group, !!checked)}
                    />
                  )}
                  <span className="text-[13px] font-medium text-foreground">
                    {formatDate(group.date)}
                  </span>
                  <span className="text-[12px] text-muted-foreground">
                    {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {reviewableCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[12px]"
                    onClick={() => handleApproveGroup(group)}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Approve All ({reviewableCount})
                  </Button>
                )}
              </div>

              {/* Items */}
              <div className="rounded-b-lg border">
                {group.items.map((item, idx) => {
                  const isReviewable = item.status === 'pending_review'
                  const isFailed = item.status === 'failed'
                  const isProcessing = item.status === 'pending_processing'
                  const config = statusConfig[item.status]
                  const category = item.category_id
                    ? categories.find((c) => c.id === item.category_id)
                    : null

                  return (
                    <div
                      key={item.id}
                      className={cn(
                        'group flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-muted/30 md:flex-row md:items-center md:gap-4',
                        idx < group.items.length - 1 && 'border-b',
                        isReviewable && 'cursor-pointer',
                      )}
                      onClick={() => isReviewable && setEditItem(item)}
                    >
                      {/* Checkbox */}
                      <div className="w-5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isReviewable && (
                          <Checkbox
                            checked={selectedIds.has(item.id)}
                            onCheckedChange={() => toggleSelect(item.id)}
                          />
                        )}
                      </div>

                      {/* Receipt thumbnail */}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted/50 overflow-hidden">
                        {item.receipt?.content_type?.startsWith('image/') && item.receipt?.url ? (
                          <img
                            src={item.receipt.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : item.receipt ? (
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </div>

                      {/* Status badge */}
                      <div className="shrink-0 md:w-24">
                        {config && (
                          <Badge variant="outline" className={cn('text-[11px] font-medium', config.className)}>
                            {isProcessing && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            {config.label}
                          </Badge>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        {isProcessing ? (
                          <span className="text-[13px] text-muted-foreground italic">
                            Analyzing receipt...
                          </span>
                        ) : (
                          <div className="flex items-baseline gap-3">
                            <span className="text-[13px] font-medium text-foreground truncate">
                              {item.memo || 'No description'}
                            </span>
                            {item.date && (
                              <span className="text-[12px] text-muted-foreground shrink-0">
                                {item.date}
                              </span>
                            )}
                          </div>
                        )}
                        {isFailed && item.error_message && (
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-destructive">
                            <AlertCircle className="h-3 w-3" />
                            <span className="truncate">{item.error_message}</span>
                          </div>
                        )}
                      </div>

                      {/* Category */}
                      <div className="w-[140px] shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isReviewable ? (
                          <Select
                            value={item.category_id?.toString() || '__none__'}
                            onValueChange={(value) => {
                              router.patch(`/reimbursement_items/${item.id}`, {
                                reimbursement_item: { category_id: value === '__none__' ? null : value },
                              }, { preserveScroll: true })
                            }}
                          >
                            <SelectTrigger className="h-7 w-full text-[12px]">
                              <SelectValue placeholder="Uncategorized" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Uncategorized</SelectItem>
                              {categories.map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : category ? (
                          <span className="text-[12px] text-muted-foreground">{category.name}</span>
                        ) : null}
                      </div>

                      {/* Amount */}
                      <div className="w-28 shrink-0 text-right">
                        {item.amount && (
                          <MoneyDisplay money={item.amount} size="sm" />
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0 items-center gap-1 md:opacity-0 transition-opacity md:group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                        {isReviewable && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => setEditItem(item)}
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-success hover:text-success"
                              onClick={() => handleApprove(item.id)}
                              title="Approve"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {isFailed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleRetry(item.id)}
                            title="Retry"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(item.id)}
                          title="Discard"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}

        {/* Bulk approve toolbar */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-0 left-0 md:left-[240px] right-0 border-t bg-card px-4 md:px-8 py-3 flex items-center justify-between shadow-lg z-30">
            <span className="text-[13px] text-muted-foreground">
              {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </Button>
              <Button size="sm" onClick={handleBulkApprove}>
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Approve Selected
              </Button>
            </div>
          </div>
        )}
      </PageContent>

      {/* Edit dialog */}
      <EditItemDialog
        item={editItem}
        categories={categories}
        onClose={() => setEditItem(null)}
      />

      <ConfirmDialog
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        title="Clear inbox?"
        description={`This will permanently remove ${inboxItemCount} inbox item${inboxItemCount === 1 ? '' : 's'}. Approved reimbursements and transactions will not be changed.`}
        confirmLabel="Clear Inbox"
        variant="destructive"
        onConfirm={handleClearInbox}
      />
    </>
  )
}
